<template>
  <div class="tw:sm:mx-4 tw:lg:mx-0 tw:relative tw:mb-6 tw:w-full">
    <div v-if="initialDataLoading" class="tw:rounded-xl tw:animate-pulse tw:bg-gray-300 tw:h-[658px]"></div>
    <div v-else-if="requiredDataHasErrors">
      <p class="tw:text-center tw:text-xl">{{ $t('onboarding.appLaunchError.header') }}</p>
    </div>
    <BaseCard v-else no-padding>
      <BudgetStatus
        class="tw:p-5 tw:md:mb-3"
        :budget="budget"
        :CPCLevel="campaignCurrentCPCLevel"
        :currency="currency"
        :expectedConsumption="expectedConsumption"
      />
      <hr class="tw:bg-gray-300 tw:h-px tw:border-0" />
      <MonthlyBudget
        class="tw:p-5 tw:pb-6"
        :budget="budget"
        :currency="currency"
        :minBudget="minBudget"
        :maxBudget="maxBudget"
        :minRecommendedBudget="budgetRecommendedMin"
        :maxRecommendedBudget="budgetRecommendedMax"
        @onBudgetSave="onBudgetSave"
      />
    </BaseCard>
    <FullScreenLoader v-show="requiredDataIsBeingRefetched || isUpdatingCampaignBudget" />
  </div>
</template>

<script setup lang="ts">
import { computed, type ComputedRef } from 'vue'

import BaseCard from '@/components/dashboard/BaseCard.vue'
import BudgetStatus from '@/components/rateConnect/budget/BudgetStatus.vue'
import MonthlyBudget from '@/components/rateConnect/budget/MonthlyBudget.vue'
import FullScreenLoader from '@/components/base/loaders/FullScreenLoader.vue'

import {
  useBudgetRecommendation,
  usePartners,
  useCampaign,
  useCampaignMutation,
  useBudget,
} from '@/components/rateConnect/queries'
import useToastNotifications from '@/components/notifications/useToastNotifications'
import { selectedAccommodationId } from '@/layouts/queries'
import { useSessionValues } from '@/composables/useSessionValues'

const { displayNotification, showSaveError } = useToastNotifications()
const { currency } = useSessionValues()

const {
  isFetching: isCampaignsFetching,
  isLoading: isCampaignsLoading,
  isError: isCampaignsError,
  campaignsData,
  currentPartner,
} = useCampaign(selectedAccommodationId)

const campaignType = computed(() => campaignsData.value?.campaignType)

const {
  isFetching: isPartnersFetching,
  isLoading: isPartnersLoading,
  isError: isPartnersError,
} = usePartners(selectedAccommodationId)

const {
  isFetching: isBudgetRecommendationFetching,
  isInitialLoading: isBudgetRecommendationLoading,
  isError: iisBudgetRecommendationError,
  budgetRecommendationData,
  minBudget,
  maxBudget,
  budgetRecommendedMin,
  budgetRecommendedMax,
} = useBudgetRecommendation(currency, currentPartner)

const { isUpdatingCampaignBudget, updateCampaignBudget } = useCampaignMutation()

const {
  budget,
  isFetching: isBudgetFetching,
  isBudgetInitalLoading,
  isError: isBudgetError,
} = useBudget({ accommodationId: selectedAccommodationId, campaignType })

const initialDataLoading: ComputedRef<boolean> = computed(
  () =>
    isBudgetRecommendationLoading.value ||
    isPartnersLoading.value ||
    isBudgetInitalLoading.value ||
    isCampaignsLoading.value
)

const requiredDataHasErrors: ComputedRef<boolean> = computed(
  () => iisBudgetRecommendationError.value || isPartnersError.value || isBudgetError.value || isCampaignsError.value
)

const requiredDataIsBeingRefetched: ComputedRef<boolean> = computed(
  () =>
    !initialDataLoading.value &&
    (isBudgetRecommendationFetching.value ||
      isBudgetFetching.value ||
      isPartnersFetching.value ||
      isCampaignsFetching.value)
)

// RateConnectCampaignResponse has no `campaigns` field, so this was always undefined at runtime
const campaignCurrentCPCLevel = computed<string | undefined>(() => undefined)
const expectedConsumption = computed(() => budgetRecommendationData?.value?.expectedConsumption)

const onBudgetSave = (newBudget: number): void => {
  updateCampaignBudget(
    {
      accommodationId: selectedAccommodationId.value,
      budget: newBudget * 100,
      currencyCode: currency.value,
    },
    {
      onSuccess: () => {
        displayNotification({
          title: 'setting_updated_success',
          titleTranslationKey: true,
          message: 'rc_budget_success',
          isTranslationKey: true,
          isHtml: true,
          type: 'success',
        })
      },
      onError: () => {
        showSaveError()
      },
    }
  )
}
</script>
